import { AuthError, User, Session } from '@supabase/supabase-js';
import { z } from 'zod';
import { supabase } from './supabase';

// Schema validation for auth inputs
const emailSchema = z.string().email('Please enter a valid email address');
const passwordSchema = z
  .string()
  .min(6, 'Password must be at least 6 characters')
  .max(128, 'Password must be less than 128 characters');

// Error handling utility
class AuthenticationError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'AuthenticationError';
  }
}

// Get auth error message without throwing
function getAuthErrorMessage(error: AuthError): string {
  let message = 'An authentication error occurred';
  
  switch (error.status) {
    case 400:
      if (error.message?.includes('Invalid login credentials')) {
        message = 'Invalid email or password';
      } else {
        message = 'Invalid email or password';
      }
      break;
    case 401:
      message = 'Your session has expired. Please sign in again';
      break;
    case 422:
      if (error.message?.includes('Password')) {
        message = 'Password must be at least 6 characters long';
      } else if (error.message?.includes('Email')) {
        message = 'Please enter a valid email address';
      } else {
        message = 'Invalid input data. Please check your email and password.';
      }
      break;
    case 429:
      message = 'Too many attempts. Please try again later';
      break;
    case 500:
      if (error.message?.includes('Database error')) {
        message = 'A temporary database error occurred. Please try again in a moment.';
      } else {
        message = 'A server error occurred. Please try again later.';
      }
      break;
    default:
      message = error.message || 'An authentication error occurred';
  }
  
  return message;
}

// Validate and format auth error messages (throws for backward compatibility)
function handleAuthError(error: AuthError): never {
  throw new AuthenticationError(getAuthErrorMessage(error), error.status?.toString());
}

export async function validateEmail(email: string): Promise<string> {
  return emailSchema.parseAsync(email);
}

export async function validatePassword(password: string): Promise<string> {
  return passwordSchema.parseAsync(password);
}

export async function signUp(email: string, password: string, userData?: any): Promise<{ user: User | null; error: string | null }> {
  try {
    console.log('Starting signup process for:', email);
    
    // Validate inputs
    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) {
      return { user: null, error: emailResult.error.errors[0].message };
    }

    const passwordResult = passwordSchema.safeParse(password);
    if (!passwordResult.success) {
      return { user: null, error: passwordResult.error.errors[0].message };
    }

    // Prepare user metadata
    const metadata = {
      full_name: userData?.full_name || '',
      has_partner: userData?.has_partner || false,
      partner_document_access: userData?.partner_document_access || '',
      children_age_groups: userData?.children_age_groups || [],
      dependents: userData?.dependents || [],
      onboarding_completed: userData?.onboarding_completed || false
    };

    // Attempt signup with retry logic
    let attempts = 0;
    const maxAttempts = 3;
    
    while (attempts < maxAttempts) {
      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: metadata
          }
        });

        if (error) {
          console.error('Signup error:', error.message);
          
          // If it's a 500 error, try again
          if (error.message.includes('500') || error.message.includes('Database error')) {
            attempts++;
            if (attempts < maxAttempts) {
              console.log(`Attempt ${attempts} failed, retrying...`);
              await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
              continue;
            }
            return { 
              user: null, 
              error: 'Signup service temporarily unavailable. Please try again in a moment.' 
            };
          }
          
          return { user: null, error: error.message };
        }

        if (data.user) {
          console.log('User created successfully:', data.user.id);
          
          // CRITICAL FIX: Ensure profile is created for the user with proper error handling
          let profileCreated = false;
          let profileAttempts = 0;
          const maxProfileAttempts = 5;
          let lastProfileError = null;
          
          while (!profileCreated && profileAttempts < maxProfileAttempts) {
            try {
              // First check if profile already exists
              const { data: existingProfile, error: checkError } = await supabase
                .from('profiles')
                .select('id')
                .eq('id', data.user.id)
                .maybeSingle();
              
              if (checkError) {
                console.error('Profile check error:', checkError);
                lastProfileError = checkError;
                profileAttempts++;
                await new Promise(resolve => setTimeout(resolve, 500 * profileAttempts));
                continue;
              }
              
              if (existingProfile) {
                console.log('Profile already exists for user:', data.user.id);
                profileCreated = true;
                break;
              }
              
              // Create profile if it doesn't exist
              const { error: profileError } = await supabase
                .from('profiles')
                .insert({
                  id: data.user.id,
                  email: data.user.email,
                  full_name: metadata.full_name,
                  avatar_url: '',
                  idecide_score: 0,
                  has_partner: metadata.has_partner,
                  partner_document_access: metadata.partner_document_access,
                  children_age_groups: metadata.children_age_groups,
                  dependents: metadata.dependents,
                  onboarding_completed: metadata.onboarding_completed,
                  payment_status: 'free',
                  payment_intent_id: null,
                  payment_completed: true,
                  payment_date: null,
                  subscription_status: 'free'
                });
              
              if (!profileError) {
                profileCreated = true;
                console.log('Profile created successfully for user:', data.user.id);
              } else {
                lastProfileError = profileError;
                profileAttempts++;
                console.error(`Profile creation failed (attempt ${profileAttempts}):`, profileError);
                await new Promise(resolve => setTimeout(resolve, 500 * profileAttempts));
              }
            } catch (profileErr) {
              console.error('Profile creation exception:', profileErr);
              lastProfileError = profileErr;
              profileAttempts++;
              await new Promise(resolve => setTimeout(resolve, 500 * profileAttempts));
            }
          }
          
          if (!profileCreated) {
            console.error('Failed to create profile after all attempts:', lastProfileError);
            return { 
              user: data.user, 
              error: 'Account created, but profile could not be created. Please contact support.' 
            };
          }
          
          return { user: data.user, error: null };
        }

        return { user: null, error: 'Sign-up failed. Please try again.' };
        
      } catch (error) {
        console.error('Signup attempt failed:', error);
        attempts++;
        if (attempts >= maxAttempts) {
          return { 
            user: null, 
            error: 'Unable to create account at this time. Please try again later.' 
          };
        }
        await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
      }
    }

    return { user: null, error: 'Sign-up failed after multiple attempts. Please try again later.' };
    
  } catch (error) {
    console.error('Sign-up exception:', error);
    return { 
      user: null, 
      error: 'An unexpected error occurred. Please try again.' 
    };
  }
}

export async function signIn(email: string, password: string): Promise<{ user: User | null; error: string | null }> {
  try {
    // Validate inputs
    await validateEmail(email);
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { user: null, error: getAuthErrorMessage(error) };
    }

    if (data.user) {
      // Ensure profile exists for the user
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();

        if (profileError || !profileData) {
          console.log('🔄 Profile not found, creating...');
          // Create profile if it doesn't exist
          const { error: createError } = await supabase
            .from('profiles')
            .insert({
              id: data.user.id,
              email: data.user.email || '',
              full_name: data.user.user_metadata?.full_name || '',
              avatar_url: data.user.user_metadata?.avatar_url || '',
              idecide_score: 0,
              has_partner: data.user.user_metadata?.has_partner || false,
              partner_document_access: data.user.user_metadata?.partner_document_access || '',
              children_age_groups: data.user.user_metadata?.children_age_groups || [],
              dependents: data.user.user_metadata?.dependents || [],
              onboarding_completed: data.user.user_metadata?.onboarding_completed || false,
              payment_status: 'free',
              payment_intent_id: null,
              payment_completed: true,
              payment_date: null,
              subscription_status: 'free'
            });

          if (createError) {
            console.error('❌ Failed to create profile during sign in:', createError);
            // Don't fail the sign in for profile creation errors
          } else {
            console.log('✅ Profile created successfully during sign in');
          }
        }
      } catch (profileErr) {
        console.error('❌ Profile check/creation failed during sign in:', profileErr);
        // Don't fail the sign in for profile errors
      }
    }

    return { user: data.user, error: null };
  } catch (error) {
    if (error instanceof AuthError) {
      return { user: null, error: getAuthErrorMessage(error) };
    }
    return { user: null, error: 'An unexpected error occurred during sign-in.' };
  }
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function resetPassword(email: string) {
  try {
    await validateEmail(email);
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });

    if (error) throw error;
  } catch (error) {
    if (error instanceof AuthError) {
      handleAuthError(error);
    }
    throw error;
  }
}

export async function updatePassword(newPassword: string) {
  try {
    await validatePassword(newPassword);
    
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;
  } catch (error) {
    if (error instanceof AuthError) {
      handleAuthError(error);
    }
    throw error;
  }
}

// Session management
export async function getSession(): Promise<Session | null> {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
}

export async function refreshSession(): Promise<void> {
  const { error } = await supabase.auth.refreshSession();
  if (error) throw error;
}

// Social auth providers
export async function signInWithProvider(provider: 'google' | 'github' | 'facebook') {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });

    if (error) throw error;
    return data;
  } catch (error) {
    if (error instanceof AuthError) {
      handleAuthError(error);
    }
    throw error;
  }
}